import React from "react";
import { motion } from "framer-motion";
import { Reveal, SectionHeading, GlowOrb } from "@/components/primitives";
import { Icon } from "@/lib/icons";
import {
  WHY_VARDHIX, LIFECYCLE, ARCHITECTURE_NODES, SECURITY_CARDS, INDUSTRIES,
} from "@/lib/contentV2";

/* -------- Why Businesses Choose Vardhix -------- */
export function WhyVardhix() {
  return (
    <section className="relative py-24 border-t border-white/10" data-testid="why-vardhix-section">
      <GlowOrb className="h-80 w-80 top-10 -left-20" color="rgba(0,212,170,0.08)" />
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading overline="Why Vardhix" title="Why businesses choose Vardhix" sub="Institutional-grade engineering, security and scale — built for the Indian market." />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {WHY_VARDHIX.map((c, i) => (
            <Reveal key={c.title} delay={(i % 3) * 0.08}>
              <motion.div whileHover={{ y: -6 }} transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }} className="group h-full glass rounded-2xl p-8 hover:border-teal/40 relative overflow-hidden">
                <div className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-teal/10 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-teal/10 border border-teal/30 text-teal">
                  <Icon name={c.icon} className="h-6 w-6" />
                </span>
                <h3 className="mt-6 text-xl font-heading font-semibold text-white">{c.title}</h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">{c.desc}</p>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------- Product Development Lifecycle -------- */
export function Lifecycle() {
  return (
    <section className="relative py-24 border-t border-white/10" data-testid="lifecycle-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading center overline="How We Build" title="Product Development Lifecycle" sub="A disciplined, repeatable process from research to continuous improvement." />
        <div className="relative mt-16">
          <div className="absolute left-0 right-0 top-7 hidden lg:block h-px bg-gradient-to-r from-transparent via-teal/40 to-transparent" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-6">
            {LIFECYCLE.map((s, i) => (
              <Reveal key={s.step} delay={i * 0.08} className="relative">
                <div className="flex flex-col items-center text-center">
                  <div className="relative z-10 flex h-14 w-14 items-center justify-center rounded-2xl bg-matte border border-teal/40 text-teal shadow-[0_0_20px_rgba(0,212,170,0.2)]">
                    <Icon name={s.icon} className="h-6 w-6" />
                    <span className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-teal text-[11px] font-bold text-navy">{i + 1}</span>
                  </div>
                  <h4 className="mt-4 text-sm font-heading font-semibold text-white">{s.step}</h4>
                  <p className="mt-1.5 text-xs text-slate-400 leading-snug">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------- Technology Architecture Diagram -------- */
export function Architecture() {
  return (
    <section className="relative py-24 border-t border-white/10 overflow-hidden" data-testid="architecture-section">
      <GlowOrb className="h-96 w-96 top-0 right-1/4" color="rgba(26,232,189,0.08)" />
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading center overline="System Design" title="Technology Architecture" sub="A layered, cloud-native architecture engineered for performance, observability and scale." />
        <Reveal delay={0.1}>
          <div className="mt-16 glass rounded-3xl p-6 md:p-10">
            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
              {ARCHITECTURE_NODES.map((n, i) => (
                <React.Fragment key={n.label}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.09, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    whileHover={{ y: -4 }}
                    className="group relative flex flex-col items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-4 py-4 min-w-[104px] hover:border-teal/50 transition-colors"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal/10 border border-teal/30 text-teal">
                      <Icon name={n.icon} className="h-5 w-5" />
                    </span>
                    <span className="text-xs font-medium text-white text-center leading-tight">{n.label}</span>
                    <span className="text-[9px] uppercase tracking-wider text-slate-500">{n.tier}</span>
                  </motion.div>
                  {i < ARCHITECTURE_NODES.length - 1 && (
                    <motion.span
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.09 + 0.05 }}
                      className="hidden md:flex items-center text-teal/60"
                    >
                      <Icon name="ArrowRight" className="h-4 w-4 animate-pulse-glow" />
                    </motion.span>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* -------- Security & Reliability -------- */
export function Security() {
  return (
    <section className="relative py-24 border-t border-white/10" data-testid="security-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading overline="Security & Reliability" title="Built to be trusted" sub="Security, availability and monitoring are engineered in — not bolted on." />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SECURITY_CARDS.map((c, i) => (
            <Reveal key={c.title} delay={(i % 3) * 0.07}>
              <div className="group h-full glass rounded-2xl p-8 hover:border-teal/40 transition-colors">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-teal/10 border border-teal/30 text-teal">
                  <Icon name={c.icon} className="h-5 w-5" />
                </span>
                <h3 className="mt-5 text-lg font-heading font-semibold text-white">{c.title}</h3>
                <p className="mt-2.5 text-sm text-slate-400 leading-relaxed">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------- Industries We Serve -------- */
export function Industries() {
  return (
    <section className="relative py-24 border-t border-white/10" data-testid="industries-section">
      <GlowOrb className="h-80 w-80 bottom-0 left-1/3" color="rgba(0,212,170,0.07)" />
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading overline="Industries" title="Industries we serve" />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {INDUSTRIES.map((c, i) => (
            <Reveal key={c.title} delay={(i % 3) * 0.07}>
              <motion.div whileHover={{ y: -6 }} transition={{ duration: 0.3 }} className="group relative h-full glass rounded-2xl p-8 overflow-hidden hover:border-teal/40">
                <div className="pointer-events-none absolute inset-x-0 -bottom-20 h-40 bg-gradient-to-t from-teal/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-teal/10 border border-teal/30 text-teal">
                  <Icon name={c.icon} className="h-6 w-6" />
                </span>
                <h3 className="mt-6 text-xl font-heading font-semibold text-white">{c.title}</h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">{c.desc}</p>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
